import { useMemo, useState } from 'react';
import { cn } from '@/shared/utils/cn';
import {
  getPdpCertificationHeading,
  getVisiblePdpCertifications,
  type PdpCertificationItem,
} from '../data/pdpCertifications.data';

interface PdpCertificationsProps {
  metalType: string;
  diamondStoneType: string | null;
  solitaireTotalNoOfStones: number | null;
  onCertificationClick?: (certificationName: string) => void;
}

function CertificationLogo({ certification }: { certification: PdpCertificationItem }) {
  return (
    <img
      src={certification.image}
      alt={certification.label}
      className={cn(
        'block max-h-[25px] max-w-[94px] object-contain',
        certification.imageClassName
      )}
    />
  );
}

function IgiCertificationContent() {
  return (
    <>
      <p>
        The International Gemological Institute (IGI) is the largest independent lab for grading
        and evaluating diamonds and fine jewelry. It is renowned for its quality services,
        extensive experience and expertise. It is located in New York City, Mumbai, Kolkata, New
        Delhi, Surat, Los Angeles, Toronto, Hong Kong, Dubai, and Tokyo. IGI, founded in 1975, is
        the only international certification lab wholly owned and controlled by one central
        governing body, which ensures consistency in IGI reports across the globe.
      </p>
      <p className="italic">
        In order to get IGI certificate with your order, please{' '}
        <a href="tel:18004190066" className="text-[var(--link-blue)] hover:underline">
          click here
        </a>{' '}
        to contact our Customer support team.
      </p>
    </>
  );
}

function CertificationContent({ certification }: { certification: PdpCertificationItem }) {
  if (certification.id === 'igi') {
    return <IgiCertificationContent />;
  }

  return (
    <>
      {certification.paragraphs.map((paragraph) => (
        <p key={paragraph}>{paragraph}</p>
      ))}
    </>
  );
}

export function PdpCertifications({
  metalType,
  diamondStoneType,
  solitaireTotalNoOfStones,
  onCertificationClick,
}: PdpCertificationsProps) {
  const certifications = useMemo(
    () => getVisiblePdpCertifications({ metalType, diamondStoneType, solitaireTotalNoOfStones }),
    [metalType, diamondStoneType, solitaireTotalNoOfStones]
  );
  const heading = useMemo(
    () => getPdpCertificationHeading({ metalType, diamondStoneType, solitaireTotalNoOfStones }),
    [metalType, diamondStoneType, solitaireTotalNoOfStones]
  );

  const [activeId, setActiveId] = useState<PdpCertificationItem['id'] | null>(null);
  const resolvedActiveId = activeId ?? certifications[0]?.id ?? null;
  const activeCertification = certifications.find((item) => item.id === resolvedActiveId);

  if (certifications.length === 0 || !activeCertification) {
    return null;
  }

  return (
    <div>
      <div className="bg-[#f5f5f5] px-4 py-2 text-center text-[13px] font-semibold leading-snug text-black">
        {heading}
      </div>

      <div className="overflow-x-auto overscroll-x-contain px-[15px] py-4 [-webkit-overflow-scrolling:touch]">
        <div className="flex w-max flex-nowrap items-stretch gap-2">
          {certifications.map((certification) => {
            const isActive = certification.id === resolvedActiveId;

            return (
              <button
                key={certification.id}
                type="button"
                onClick={() => {
                  setActiveId(certification.id);
                  onCertificationClick?.(certification.label);
                }}
                className={cn(
                  'box-border flex h-[59px] w-[118px] shrink-0 items-center justify-center border border-[#e2e2e2] bg-white px-2 shadow-[0_1px_2px_rgba(0,0,0,0.2)]',
                  isActive
                    ? 'border-b-[3px] border-b-[var(--link-blue)]'
                    : 'border-b border-b-[#e2e2e2]'
                )}
                aria-pressed={isActive}
                aria-label={certification.label}
              >
                <CertificationLogo certification={certification} />
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-4 px-[15px] pb-[18px] text-[13px] leading-relaxed text-black">
        <CertificationContent certification={activeCertification} />
      </div>
    </div>
  );
}
